import { useEffect, useState } from 'react';
import { ChefHat, GlassWater, Plus, Trash2, Loader2 } from 'lucide-react';
import { api } from '../lib/spizirnaUi';
import usePotvrzeni from './ui/usePotvrzeni';
import useJeMobil from '../hooks/useJeMobil';

const BARVY = ['#3b82f6', '#FF8A00', '#a855f7', '#10b981', '#ef4444', '#eab308'];

/**
 * Správa → Provozovna → Kuchyně a bar (dřív Nastavení → Kuchyně a bar).
 * Barvy stanic jsou data (rozlišení displejů), ne akcent modulu.
 *
 * Kuchyně jsou očíslované a tvoří kaskádu: položka se zobrazí na své
 * kuchyni, a když ta nemá připojený displej, spadne na nejbližší nižší,
 * až po Kuchyni 1 (hlavní — fronta, sloty, teplo, účtenky). Bar je mimo
 * kaskádu a jede vlastní frontu. Který displej je která stanice, se volí
 * v Nastavení pokladny na terminálu; co kam patří, u kategorie produktu.
 */
export default function KdsStaniceSprava({ isDark, barva = '#3B82F6' }) {
    const isMobile = useJeMobil();
    const [potvrdit, potvrzeni] = usePotvrzeni(isDark);
    const [stanice, setStanice] = useState(null);
    const [chyba, setChyba] = useState('');

    const nacti = () => api('/api/kds-stanice').then(setStanice).catch(() => setStanice([]));
    useEffect(() => { nacti(); }, []);

    const muted = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';
    const border = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';
    const fieldBg = isDark ? 'rgba(255,255,255,0.05)' : '#fff';

    const proved = async (fn) => {
        setChyba('');
        try { await fn(); } catch (e) { setChyba(e?.error || e?.message || 'Nepodařilo se uložit.'); }
        nacti();
    };
    const uprav = (s, zmena) => {
        setStanice(prev => prev.map(x => x.id === s.id ? { ...x, ...zmena } : x));
        return proved(() => api(`/api/kds-stanice/${s.id}`, 'PUT', zmena));
    };

    const pridatKuchyni = () => {
        const dalsi = Math.max(0, ...(stanice || []).filter(s => s.druh === 'kuchyne').map(s => s.cislo || 0)) + 1;
        proved(() => api('/api/kds-stanice', 'POST', {
            nazev: `Kuchyně ${dalsi}`, druh: 'kuchyne', cislo: dalsi, barva: BARVY[(dalsi - 1) % BARVY.length],
        }));
    };
    const pridatBar = () => proved(() => api('/api/kds-stanice', 'POST', {
        nazev: 'Bar', druh: 'bar', barva: '#10b981', max_slotu: 6,
    }));

    if (!stanice) return <Loader2 size={18} className="animate-spin" style={{ color: muted }} />;

    const maBar = stanice.some(s => s.druh === 'bar');

    return (
        <div className="space-y-4 max-w-2xl">
            <div>
                <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Kuchyně a bar</h3>
                <p className="text-xs mt-1" style={{ color: muted }}>
                    Kuchyně tvoří kaskádu: položka pro Kuchyni 3 bez připojeného displeje spadne na Kuchyni 2, a když není ani ta, na Kuchyni 1.
                    Kuchyně 1 je hlavní — řídí frontu, posílá do tepla a tiskne. Bar jede samostatně a do tepla nic neposílá.
                    Co kam patří, nastavíš u kategorie produktu; který displej je která stanice, v Správa → Provozovna → Pokladny a telefon.
                </p>
            </div>

            {chyba && <p className="text-xs" style={{ color: '#EF4444' }}>{chyba}</p>}

            <div className="space-y-2">
                {stanice.map(s => {
                    const hlavni = s.druh === 'kuchyne' && s.cislo === 1;
                    return (
                        <div key={s.id} className="flex flex-wrap items-center gap-3 rounded-xl px-4 py-3"
                            style={{ background: isDark ? 'rgba(255,255,255,0.03)' : 'var(--surface-base)', border: `1px solid ${border}`, opacity: s.aktivni ? 1 : 0.5 }}>
                            {s.druh === 'bar'
                                ? <GlassWater size={18} style={{ color: s.barva || muted }} />
                                : <ChefHat size={18} style={{ color: s.barva || muted }} />}
                            <input value={s.nazev}
                                onChange={e => setStanice(prev => prev.map(x => x.id === s.id ? { ...x, nazev: e.target.value } : x))}
                                onBlur={e => uprav(s, { nazev: e.target.value })}
                                className="text-sm rounded-lg px-2 py-1 font-semibold"
                                style={{ background: fieldBg, border: `1px solid ${border}`, color: 'var(--text-primary)', width: isMobile ? '100%' : 150, minHeight: isMobile ? 40 : undefined }} />
                            <span className="text-[11px] font-semibold px-2 py-0.5 rounded"
                                style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)', color: muted }}>
                                {s.druh === 'bar' ? 'bar · mimo kaskádu' : hlavni ? 'Kuchyně 1 · hlavní' : `kaskáda ${s.cislo}`}
                            </span>
                            <div className="flex items-center gap-1">
                                {BARVY.map(b => (
                                    <button key={b} onClick={() => uprav(s, { barva: b })} title={b}
                                        style={{ width: isMobile ? 32 : 18, height: isMobile ? 32 : 18, borderRadius: isMobile ? 8 : 5, background: b, cursor: 'pointer',
                                            border: s.barva === b ? '2px solid var(--text-primary)' : '1px solid rgba(0,0,0,0.2)' }} />
                                ))}
                            </div>
                            {(hlavni || s.druh === 'bar') && (
                                <label className="flex items-center gap-1 text-[11px]" style={{ color: muted }}>
                                    sloty
                                    <input type="number" min="1" max="20" value={s.max_slotu ?? ''}
                                        onChange={e => setStanice(prev => prev.map(x => x.id === s.id ? { ...x, max_slotu: e.target.value } : x))}
                                        onBlur={e => uprav(s, { max_slotu: e.target.value ? parseInt(e.target.value) : null })}
                                        className="rounded px-1 py-0.5 text-xs"
                                        style={{ width: 48, background: fieldBg, border: `1px solid ${border}`, color: 'var(--text-primary)' }} />
                                </label>
                            )}
                            {!hlavni && !isMobile && (
                                <button onClick={async () => { if (await potvrdit(`Smazat stanici „${s.nazev}“?`, { text: 'Kategorie a produkty s ní spadnou na Kuchyni 1.', potvrdit: 'Smazat' })) proved(() => api(`/api/kds-stanice/${s.id}`, 'DELETE')); }}
                                    className="ml-auto" title="Smazat"
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444' }}>
                                    <Trash2 size={15} />
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>

            <div className="flex gap-2">
                <button onClick={pridatKuchyni}
                    className="flex items-center gap-1.5 text-xs font-semibold rounded-lg px-3 py-2"
                    style={{ background: barva, border: 'none', color: '#fff', cursor: 'pointer', minHeight: isMobile ? 44 : undefined }}>
                    <Plus size={13} /> Přidat kuchyni
                </button>
                {!maBar && (
                    <button onClick={pridatBar}
                        className="flex items-center gap-1.5 text-xs font-semibold rounded-lg px-3 py-2"
                        style={{ background: 'transparent', border: `1px solid ${border}`, color: 'var(--text-primary)', cursor: 'pointer', minHeight: isMobile ? 44 : undefined }}>
                        <Plus size={13} /> Přidat bar
                    </button>
                )}
            </div>
            {potvrzeni}
        </div>
    );
}
